// BottomNav.tsx — 5-tab bottom navigation bar.
// Solid orange brand background (#E46E34), white icons, gold active dot.
// Only "Discover" tab is functional — others show "coming soon" toast.
// Not fixed-position — sits in the flex column of MapPage.
// All data from Neon PostgreSQL — zero MongoDB.

import { MapPin, Calendar, Sparkles, Images, User, Menu, Wifi, Phone, LayoutDashboard, Map, X, LogIn, LogOut, Ticket } from 'lucide-react';
import { useUIStore } from '@/stores/useUIStore';
import { hasAdminRole } from '@/lib/isAdmin';   // v70.038 — one admin check for tab + layer
import { useMapStore } from '@/stores/useMapStore';
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// ── Brand colours ────────────────────────────────────────────────────────────
const BRAND_ORANGE = '#E46E34';
const GOLD         = '#F7CB64';
const WHITE        = '#FFFFFF';
const WHITE_DIM    = 'rgba(255, 255, 255, 0.58)';

// ── Tab definitions ───────────────────────────────────────────────────────────
type TabId = 'discover' | 'events' | 'activities' | 'gallery' | 'ai' | 'live' | 'me' | 'more';

interface TabDef {
  id: TabId;
  label: string;
  icon: typeof MapPin;
  mapMode: 'venues' | 'events' | 'activities' | null;
  adminOnly?: boolean;
  path?: string;   // for non-map tabs that navigate to a route
  /** CON-1378 — per-tab accent. On the orange bar a flat white icon row reads
   *  dead; each tab now carries its own hue, shown as a soft tinted disc behind
   *  the glyph when active and as a subtle wash when not. */
  tint: string;
}

function getTabs(t: (key: string) => string): TabDef[] { return [
  { id: 'discover',   label: t('nav.discover'),   icon: MapPin,    mapMode: 'venues', tint: '#0E9F6E' },
  { id: 'events',     label: t('nav.events'),     icon: Calendar,  mapMode: 'events', tint: '#B45309' },
  // CON-1378 — City Gallery is PUBLIC since v69.900 (feed + stats need no auth);
  // the nav entry was deliberately held back until it was presentable. Route-based
  // tab, so it uses the existing `path` branch in handleTab.
  { id: 'gallery',    label: t('nav.gallery'),    icon: Images,    mapMode: null, path: '/gallery', tint: '#BE185D' },
  // EV-746 — Activities tab. NOW LIVE FOR EVERYONE (Patrick 2026-07-23: "make
  // activities actually live not only for admins inside the frontend"). A MAP
  // MODE (see them on a map, filter with chips by category, same as events);
  // the feed is reached via the "Feed" button in ActivityFilters.
  { id: 'activities', label: t('nav.activities'), icon: Ticket,    mapMode: 'activities', tint: '#1D4ED8' },
  // EV-1299 — the old 'AI (coming soon)' tab is now 🛵 LIVE: filters the map to ONLY live mobile
  // street vendors (icon rendered as the 🛵 emoji below). mapMode:null — handled specially.
  // 🔴 v70.038 — ADMIN-ONLY. Patrick 2026-07-31: "Don't have that live toggle button for
  // anyone but me or admins." The tab's whole purpose is showing live vendor pins, and those
  // became staff-only in v70.036 — leaving the tab public meant a tab that opened an EMPTY
  // map for every consumer, which is worse than not having it. Tab and layer now share one
  // admin check (lib/isAdmin) so they cannot disagree.
  // 🔴 v70.047 — LIVE TAB REMOVED FROM THE NAV ENTIRELY (not just admin-gated).
  //
  // Patrick, 2026-07-31: "its bac i can see the map , but also sitll the live which i asked to
  // remove its still there fuck".
  //
  // v70.038 set adminOnly:true, which hid it from consumers but NOT from him — he is an admin,
  // so he kept seeing the thing he had asked to be gone. "Gate it to admins" and "remove it"
  // are different requests and I only did the first.
  //
  // Removing it costs nothing right now: the tab's whole job was filtering the map to live
  // vendor pins, and those pins are themselves staff-only until the hail loop is proven. The
  // caller side lives on the VENUE SHEET (HailVendorCard), which is where a customer actually
  // decides to call a cart over. Restore this entry once vendors are public again.
  // { id: 'live',    label: 'Live',       icon: Sparkles,  mapMode: null, adminOnly: true, tint: '#C2410C' },
  { id: 'me',         label: t('nav.me'),  icon: User,      mapMode: null, tint: '#334155' },
  // 'More' removed 2026-06-11 (Patrick) — confusing/near-empty. Support→/contact, account→/me.
]; }

// EV-746 — admin detection (mirrors getMoreItems): logged-in user object hl_user
// with role=admin, or the legacy user_role flag. Non-admins never see admin tabs.
// v70.038 — delegates to the shared helper (lib/isAdmin), which checks the JWT role AND the
// hl_user / user_role localStorage signals. Previously this was a second, narrower
// implementation that could disagree with the map's gate.
function isAdminUser(): boolean {
  if (typeof window === 'undefined') return false;
  return hasAdminRole();
}

// ── Component ─────────────────────────────────────────────────────────────────
function getMoreItems(t: (key: string) => string) {
  const isLoggedIn = typeof window !== 'undefined' && (
    localStorage.getItem('hl_auth_token') || localStorage.getItem('venueapp_token') || localStorage.getItem('token')
  );
  // EV-192 — Tracy (role=user) saw admin-only items. Gate dev/admin tools to admins.
  let isAdmin = false;
  try {
    const u = JSON.parse((typeof window !== 'undefined' && localStorage.getItem('hl_user')) || 'null');
    isAdmin = String(u?.role || '').toLowerCase() === 'admin';
  } catch { /* not parseable → treat as non-admin (hide admin items) */ }

  const items: Array<{ label: string; icon: typeof Wifi; path: string }> = [];
  if (!isLoggedIn) {
    items.push({ label: t('auth.signIn'), icon: LogIn, path: '/login' });
  }
  // Admin-only dev/admin tools (hidden from regular consumers like Tracy).
  if (isAdmin) {
    items.push({ label: t('me.speedTest'), icon: Wifi, path: '/speed-test' });
    // EV-193 — until a client-side venue-owner signal exists, Venue Dashboard is
    // admin-only; should also show to actual venue owners later.
    items.push({ label: t('me.venueDashboard'), icon: LayoutDashboard, path: '/venue-dashboard' });
    items.push({ label: t('nav.classicMapV1'), icon: Map, path: '/v1' });
  }
  items.push({ label: t('nav.support'), icon: Phone, path: '/contact' });  // EV-194 — renamed Contact Us → Support
  if (isLoggedIn) {
    items.push({ label: t('nav.signOut'), icon: LogOut, path: '/logout' });
  }
  return items;
}

export default function BottomNav() {
  const { t } = useTranslation();
  const activeTab  = useUIStore((s) => s.activeTab);
  const setActiveTab = useUIStore((s) => s.setActiveTab);
  const showToast  = useUIStore((s) => s.showToast);
  const setMode    = useMapStore((s) => s.setMode);
  const mapMode    = useMapStore((s) => s.mode);
  const vendorOnly = useMapStore((s) => s.vendorOnly);           // EV-1299 — 🛵 Live tab state
  const setVendorOnly = useMapStore((s) => s.setVendorOnly);
  const setMyReviewsOpen = useUIStore((s) => s.setMyReviewsOpen);
  const [moreOpen, setMoreOpen] = useState(false);
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const onMap = pathname === '/';

  // EV-746 — drop admin-only tabs (Activities) for non-admins. Computed each
  // render so a fresh login flips it without a reload.
  const TABS = getTabs(t);
  const visibleTabs = TABS.filter((tab) => !tab.adminOnly || isAdminUser());

  function handleTab(tab: TabDef) {
    if (tab.id === 'me') {
      setMyReviewsOpen(true);
      setMoreOpen(false);
      return;
    }
    if (tab.id === 'more') {
      setMoreOpen(!moreOpen);
      return;
    }
    if (tab.id === 'live') {
      // EV-1299 — 🛵 Live: show ONLY mobile street vendors on the map.
      setMoreOpen(false);
      setActiveTab('live');
      setVendorOnly(true);
      if (onMap) setMode('venues');
      else navigate('/');
      return;
    }
    // EV-746 — route-based tabs (e.g. Activities) navigate to their path.
    if (tab.path) {
      setMoreOpen(false);
      setActiveTab(tab.id);
      navigate(tab.path);
      return;
    }
    setMoreOpen(false);
    setActiveTab(tab.id);
    // EV-1299 — leaving 🛵 Live for any normal map tab clears the vendor-only filter.
    if (tab.mapMode) setVendorOnly(false);
    // On the map: just switch the map mode (no navigation). Off the map (Me,
    // Purchases, Payments, etc.): navigate to the map in the right mode so the
    // bottom nav works as global navigation — EV-691.
    if (tab.mapMode) {
      if (onMap) setMode(tab.mapMode);
      else navigate(
        tab.mapMode === 'events' ? '/?mode=events'
        : tab.mapMode === 'activities' ? '/?mode=activities'   // EV-746
        : '/',
      );
    }
  }

  return (
    <>
    {/* More menu popup */}
    {moreOpen && (
      <div style={{
        position: 'absolute', bottom: 60, right: 8, zIndex: 50,
        background: 'white', borderRadius: 14,
        boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
        padding: '8px 0', minWidth: 200,
      }}>
        {getMoreItems(t).map((item) => {
          const Icon = item.icon;
          return (
            <a
              key={item.path}
              href={item.path}
              onClick={(e) => { e.preventDefault(); setMoreOpen(false); window.location.href = item.path; }}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 16px', color: '#374151',
                textDecoration: 'none', fontSize: 14, fontWeight: 500,
              }}
            >
              <Icon size={18} color="#6b7280" />
              {item.label}
            </a>
          );
        })}
      </div>
    )}
    {/* Click-away backdrop */}
    {moreOpen && (
      <div
        onClick={() => setMoreOpen(false)}
        style={{ position: 'fixed', inset: 0, zIndex: 49 }}
      />
    )}
    <nav
      role="tablist"
      aria-label={t('nav.mainNavigationAria')}
      style={{
        display: 'flex',
        alignItems: 'stretch',
        background: BRAND_ORANGE,
        flexShrink: 0,
        // Respect iPhone home bar
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      {visibleTabs.map((tab) => {
        // For tabs with mapMode, derive active state from the actual map mode
        // so cross-link mode switches (VenueEventsSection, EventSheet) update the tab.
        // Route-based tabs (Activities) are active when on their path.
        const isActive = tab.id === 'live'
          ? vendorOnly                                   // 🛵 Live is active while the filter is on
          : tab.mapMode
            ? mapMode === tab.mapMode
            : tab.path
              ? pathname === tab.path
              : activeTab === tab.id;
        const Icon = tab.icon;
        const tint = tab.tint || WHITE;

        return (
          <button
            key={tab.id}
            role="tab"
            type="button"
            aria-selected={isActive}
            aria-label={tab.label}
            onClick={() => handleTab(tab)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 5,
              padding: '8px 4px 7px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              position: 'relative',
              minWidth: 0,
            }}
          >
            {/* CON-1378 v2 — CHIP, not a tinted outline.
                v1 tinted the glyph itself in pastel; on the orange bar that made
                the icons wash out and half-disappear (Patrick: "they disappear
                against the orange background ... not just an outline").
                Now every icon sits on a solid chip so it always has a hard edge
                against the orange, and the colour lives in the chip:
                  · ACTIVE   — white chip, saturated brand-coloured glyph, lifted
                  · INACTIVE — translucent white glass chip, solid white glyph
                Previous version backed up at
                ~/hl-backups/BottomNav.PREV-v69.911.tsx.bak */}
            {tab.id === 'live' ? (
              <span style={{
                display: 'grid', placeItems: 'center',
                width: 34, height: 34, borderRadius: 12,
                background: isActive ? '#FFFFFF' : 'rgba(255,255,255,0.16)',
                boxShadow: isActive ? '0 3px 10px rgba(0,0,0,0.22)' : 'none',
                transform: isActive ? 'translateY(-1px)' : 'none',
                transition: 'background .18s ease, box-shadow .18s ease, transform .18s ease',
                fontSize: 18, lineHeight: '19px',
              }}>🛵</span>
            ) : tab.id === 'me' && typeof window !== 'undefined' && localStorage.getItem('user_profile_image') ? (
              <img
                src={localStorage.getItem('user_profile_image') || ''}
                alt=""
                style={{
                  width: 34, height: 34, borderRadius: 12, objectFit: 'cover',
                  border: isActive ? '2.5px solid #FFFFFF' : '2px solid rgba(255,255,255,0.45)',
                  boxShadow: isActive ? '0 3px 10px rgba(0,0,0,0.22)' : 'none',
                  transform: isActive ? 'translateY(-1px)' : 'none',
                  transition: 'all .18s ease',
                }}
              />
            ) : (
              <span
                style={{
                  display: 'grid', placeItems: 'center',
                  width: 34, height: 34, borderRadius: 12,
                  background: isActive ? '#FFFFFF' : 'rgba(255,255,255,0.16)',
                  boxShadow: isActive ? '0 3px 10px rgba(0,0,0,0.22)' : 'none',
                  transform: isActive ? 'translateY(-1px)' : 'none',
                  transition: 'background .18s ease, box-shadow .18s ease, transform .18s ease',
                }}
              >
                <Icon
                  size={19}
                  color={isActive ? tint : '#FFFFFF'}
                  strokeWidth={isActive ? 2.6 : 2.2}
                  // Solid-feel glyphs: lucide is stroke-based, so a matching fill
                  // at low alpha gives weight without turning into a blob.
                  fill={isActive ? `${tint}22` : 'rgba(255,255,255,0.18)'}
                />
              </span>
            )}

            <span
              style={{
                fontSize: 10,
                fontWeight: isActive ? 800 : 600,
                color: isActive ? WHITE : 'rgba(255,255,255,0.82)',
                letterSpacing: '0.02em',
                lineHeight: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              {tab.label}
            </span>
          </button>
        );
      })}
    </nav>
    </>
  );
}
